import { z } from "zod";
import {
  merchantHomeActionPurposeSchema,
  merchantHomeBindingStateSchema,
  merchantHomeCapabilityCardTypes,
  merchantHomeIntentTypeSchema,
  merchantHomeLegacyCardTypes,
  merchantHomeStoreManagementCardTypes,
  merchantHomeWorkbenchCardTypeSchema,
} from "../merchantHomeSchemas";
import { profitComponentKeys, storeManagementSectionSchemas } from "./storeManagementSchemas";

export const WORKBENCH_CONTENT_PAGE_LIMIT = 10;
export const actionPurposeSchema = merchantHomeActionPurposeSchema;
export const contentStateSchema = z.enum(["ready", "requires_binding", "requires_setup", "empty", "unavailable", "error"]);
export const merchantAdAccountBindingIDSchema = z.string().max(128).regex(/^maab_[a-z0-9]+$/u);

export function merchantHomeAdAccountWorkspaceHref(id: string) {
  const parsed = merchantAdAccountBindingIDSchema.safeParse(id);
  return parsed.success ? `/workspace?ad_account_binding_id=${parsed.data}` : undefined;
}

export const contentScopeSchema = z.object({
  organization_id: z.string().min(1),
  shop_binding_id: z.string(),
  ad_account_binding_id: merchantAdAccountBindingIDSchema.optional(),
  market: z.string(),
  category: z.string(),
  binding_state: merchantHomeBindingStateSchema,
}).strict();

export const sectionKinds = {
  daily_product: { rationale: "text", audience: "text", suggestions: "text" },
  advertising_content: { storyboard: "storyboard", copy: "text", suggestions: "text" },
  auto_listing: { draft_details: "draft" },
  promotion_calendar: { events: "events", exchange_rate: "exchange_rate" },
  media_reference: { reference_notes: "text", suggestions: "text" },
  competitor_updates: { objects: "objects", changes: "changes" },
  creator_tracking: { objects: "objects", tracking_status: "tracking_status" },
  market_trends: { metrics: "metrics", evidence: "text" },
  monthly_opportunity: { structure: "monthly_structure", evidence: "evidence", coverage: "coverage" },
  store_opening: { progress: "opening_progress", steps: "opening_steps", material_gaps: "material_gaps" },
  pricing_calculator: { assumptions: "pricing_assumptions", breakdown: "pricing_breakdown", result: "pricing_result" },
  product_selection_analysis: { candidates: "selection_candidates", reasons: "text", evidence: "evidence" },
  creator_recommendation: { creators: "creator_candidates", match: "text", evidence: "evidence" },
  report_export: { snapshot: "report_snapshot", status: "report_status", resource: "report_resource" },
  content_strategy: { dimensions: "strategy_dimensions", source_state: "strategy_source_state" },
  policy_compliance: { rules: "policy_rules", events: "policy_events", coverage: "coverage", status: "policy_status" },
  store_operations: { performance: "store_metrics", comparison: "store_comparison", connection: "connection_health" },
  product_performance: { ranking: "product_ranking", trend: "product_trend", coverage: "source_coverage" },
  product_diagnosis: { findings: "product_findings", evidence: "safe_evidence", coverage: "source_coverage" },
  advertising_management: { accounts: "ad_accounts", campaigns: "ad_campaigns", coverage: "source_coverage" },
  advertising_performance: { performance: "ad_metrics", trend: "ad_trend", attribution: "ad_attribution" },
  inventory_management: { summary: "inventory_summary", low_stock: "low_stock_items", coverage: "source_coverage" },
  fulfillment_logistics: { summary: "fulfillment_summary", exceptions: "fulfillment_exceptions", status_mapping: "status_mapping" },
  after_sales_reviews: { summary: "after_sales_summary", aging: "after_sales_aging", todos: "safe_todos" },
  profit_settlement: { components: "profit_components", result: "profit_result", settlement: "settlement_status" },
  social_media: { accounts: "social_accounts", queue: "social_queue", performance: "social_performance" },
  email_management: { accounts: "email_accounts", inbox: "email_inbox", sync: "email_sync" },
} as const;

const nonEmpty = (limit: number) => z.string().min(1).max(limit)
  .refine(value => value === value.trim(), "字段不能包含首尾空白")
  .refine(value => [...value].every(character => {
    const code = character.codePointAt(0) ?? 0;
    return code >= 0x20 || code === 0x0a || code === 0x09;
  }), "字段不能包含控制字符");
const timestamp = z.string().datetime({ offset: true });
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u).refine(value => {
  const [year, month, day] = value.split("-").map(Number);
  if (year === undefined || month === undefined || day === undefined || month < 1 || month > 12 || day < 1) return false;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= (days[month - 1] ?? 0);
}, "日期必须是真实的 YYYY-MM-DD");
const sourceTime = z.union([timestamp, dateOnly]);

function sourceTimeNanos(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/u.test(value)) return BigInt(Date.parse(`${value}T00:00:00Z`)) * 1_000_000n;
  const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})$/u.exec(value);
  if (!match) return null;
  const milliseconds = Date.parse(`${match[1]}${match[3]}`);
  if (Number.isNaN(milliseconds)) return null;
  return BigInt(milliseconds) * 1_000_000n + BigInt((match[2] ?? "").slice(0, 9).padEnd(9, "0"));
}

function exactDuration(start: string, end: string, hours: number) {
  const from = sourceTimeNanos(start); const to = sourceTimeNanos(end);
  return from !== null && to !== null && to - from === BigInt(hours) * 3_600_000_000_000n;
}

export const sourceCoverageReasonSchema = z.enum([
  "cwm_daily_known_fields_only",
  "cwm_ad_cut_known_fields_only",
  "cwm_trend_text_only",
  "authorized_draft_scope_complete",
  "government_holidays_without_platform_campaigns",
  "calendar_events_unavailable_exchange_rate_only",
  "qualified_media_without_coverage_contract",
  "monitor_projection_without_remote_ack",
  "cwm_monthly_published_snapshot",
  "opening_case_safe_projection",
  "deterministic_pricing_preview",
  "versioned_product_selection_artifact",
  "versioned_creator_recommendation_artifact",
  "cloud_report_resource_available",
  "experience_strategy_current_safe_projection",
  "experience_strategy_current_missing_dimensions",
  "policy_management_only",
  "source_not_evaluated",
  "source_unavailable",
  "source_read_failed",
]);

const sourceStatusBaseShape = {
  window: z.object({ start: timestamp, end_exclusive: timestamp }).strict().nullable(),
  as_of: sourceTime.nullable(),
  freshness: z.object({
    state: z.enum(["fresh", "stale", "unknown"]),
    basis: z.enum(["max_age", "authoritative_read", "source_time_unavailable", "not_evaluated"]),
    stale_after: sourceTime.nullable(),
  }).strict(),
};

function refineSourceStatusTiming(status: {
  window: { start: string; end_exclusive: string } | null;
  as_of: string | null;
  freshness: { state: "fresh" | "stale" | "unknown"; basis: "max_age" | "authoritative_read" | "source_time_unavailable" | "not_evaluated"; stale_after: string | null };
}, ctx: z.RefinementCtx) {
  if (status.window && sourceTimeNanos(status.window.start)! >= sourceTimeNanos(status.window.end_exclusive)!) ctx.addIssue({ code: "custom", message: "来源窗口必须是递增的半开区间", path: ["window"] });
  const { state, basis, stale_after: staleAfter } = status.freshness;
  if (basis === "max_age") {
    if (state === "unknown" || status.as_of === null || staleAfter === null || sourceTimeNanos(staleAfter)! <= sourceTimeNanos(status.as_of)!) ctx.addIssue({ code: "custom", message: "max_age 必须提供递增的来源时间和过期阈值", path: ["freshness"] });
  } else if (basis === "authoritative_read") {
    if (state !== "fresh" || status.as_of === null || staleAfter !== null) ctx.addIssue({ code: "custom", message: "权威读取必须有来源时间且只能为新鲜", path: ["freshness"] });
  } else if (state !== "unknown" || status.as_of !== null || staleAfter !== null) {
    ctx.addIssue({ code: "custom", message: "无来源时间或未评估时不能声明新鲜度", path: ["freshness"] });
  }
}

export const legacySourceStatusSchema = z.object({
  ...sourceStatusBaseShape,
  coverage: z.object({ state: z.enum(["complete", "partial", "unknown"]), reason_code: sourceCoverageReasonSchema }).strict(),
  source_revision: nonEmpty(1000).nullable(),
}).strict().superRefine(refineSourceStatusTiming);

const storeManagementSnapshotSchema = z.enum(merchantHomeStoreManagementCardTypes.map(type => `merchant_home.${type}.v1`) as [
  `merchant_home.${(typeof merchantHomeStoreManagementCardTypes)[number]}.v1`,
  ...`merchant_home.${(typeof merchantHomeStoreManagementCardTypes)[number]}.v1`[],
]);
export const storeManagementCoverageReasonSchema = z.enum([
  "provider_complete",
  "provider_fields_partial",
  "provider_top_n_partial",
  "provider_total_unknown",
  "local_billing_only",
  "analysis_artifact_partial",
  "safe_aggregate_only",
  "profit_components_missing",
  "connection_health_only",
]);
export const storeManagementSyncHealthSchema = z.object({
  scheduler_state: z.enum(["enabled", "disabled", "not_configured", "unsupported"]),
  latest_run_state: z.enum(["none", "running", "retry_wait", "succeeded", "partial", "failed", "canceled"]),
  failure_reason: z.enum(["provider_authorization_failed", "provider_contract_or_data_invalid", "provider_unavailable", "source_sync_failed"]).nullable(),
}).strict().superRefine((health, ctx) => {
  if ((health.latest_run_state === "failed") !== (health.failure_reason !== null)) {
    ctx.addIssue({ code: "custom", message: "同步失败状态与失败原因必须同时存在", path: ["failure_reason"] });
  }
});
export const storeManagementSourceStatusSchema = z.object({
  ...sourceStatusBaseShape,
  coverage: z.object({ state: z.enum(["complete", "partial", "unknown"]), reason_code: storeManagementCoverageReasonSchema }).strict(),
  source_revision: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,255}$/u),
  snapshot_schema: storeManagementSnapshotSchema,
  as_of_basis: z.enum(["provider_source_time", "provider_response_time", "collector_observed_time"]),
  sync_health: storeManagementSyncHealthSchema.optional(),
}).strict().superRefine((status, ctx) => {
  refineSourceStatusTiming(status, ctx);
  if (!status.window || !status.as_of || !timestamp.safeParse(status.as_of).success || status.freshness.basis !== "max_age" || status.freshness.state === "unknown" || !status.freshness.stale_after || !timestamp.safeParse(status.freshness.stale_after).success) {
    ctx.addIssue({ code: "custom", message: "经营快照必须提供可追溯窗口、来源时间和过期阈值", path: ["window"] });
  }
  const partialReasons = new Set([
    "provider_fields_partial", "provider_top_n_partial", "local_billing_only", "analysis_artifact_partial",
    "safe_aggregate_only", "profit_components_missing", "connection_health_only",
  ]);
  const coverageMatches = status.coverage.state === "complete"
    ? status.coverage.reason_code === "provider_complete"
    : status.coverage.state === "unknown"
      ? status.coverage.reason_code === "provider_total_unknown"
      : partialReasons.has(status.coverage.reason_code);
  if (!coverageMatches) ctx.addIssue({ code: "custom", message: "经营快照覆盖状态与原因不一致", path: ["coverage"] });
});

export const sourceStatusSchema = z.union([legacySourceStatusSchema, storeManagementSourceStatusSchema]);

export function legacySourceStatus(): z.infer<typeof legacySourceStatusSchema> {
  return {
    window: null,
    as_of: null,
    freshness: { state: "unknown", basis: "not_evaluated", stale_after: null },
    coverage: { state: "unknown", reason_code: "source_not_evaluated" },
    source_revision: null,
  };
}

const privateIPv4 = (hostname: string) => {
  const parts = hostname.split(".");
  if (parts.length !== 4 || parts.some(part => !/^\d{1,3}$/u.test(part) || Number(part) > 255)) return false;
  const [first = 0, second = 0] = parts.map(Number);
  return first === 0 || first === 10 || first === 127 || (first === 169 && second === 254) || (first === 172 && second >= 16 && second <= 31) || (first === 192 && second === 168);
};

const mappedIPv4 = (hostname: string) => {
  if (!hostname.startsWith("::ffff:")) return "";
  const tail = hostname.slice("::ffff:".length);
  if (tail.includes(".")) return tail;
  const words = tail.split(":");
  if (words.length !== 2 || words.some(word => !/^[0-9a-f]{1,4}$/u.test(word))) return "";
  const high = Number.parseInt(words[0]!, 16); const low = Number.parseInt(words[1]!, 16);
  return `${high >> 8}.${high & 255}.${low >> 8}.${low & 255}`;
};

export function isSafeWorkbenchPublicURL(value: string) {
  if (!value || value.length > 2048) return false;
  try {
    const url = new URL(value);
    const hostname = url.hostname.replace(/^\[|\]$/gu, "").toLowerCase();
    if (url.protocol !== "https:" || !hostname || url.username || url.password || url.hash) return false;
    const mapped = mappedIPv4(hostname);
    if (hostname === "localhost" || hostname.endsWith(".localhost") || hostname.endsWith(".local") || privateIPv4(hostname) || Boolean(mapped && privateIPv4(mapped))) return false;
    if (hostname === "::" || hostname === "::1" || hostname.startsWith("fc") || hostname.startsWith("fd") || hostname.startsWith("fe8") || hostname.startsWith("fe9") || hostname.startsWith("fea") || hostname.startsWith("feb")) return false;
    if (url.search.includes(";")) return false;
    for (const part of url.search.slice(1).split("&").filter(Boolean)) {
      let key: string;
      try {
        const separator = part.indexOf("=");
        const rawKey = separator < 0 ? part : part.slice(0, separator);
        const rawValue = separator < 0 ? "" : part.slice(separator + 1);
        key = decodeURIComponent(rawKey.replaceAll("+", " ")).toLowerCase();
        decodeURIComponent(rawValue.replaceAll("+", " "));
      } catch { return false; }
      if (key.includes("token") || key.includes("secret") || key.includes("signature") || key.includes("credential") || ["code", "authorization", "auth", "api_key", "apikey"].includes(key) || (key === "key" && hostname !== "media.merry.center") || key.startsWith("x-amz-") || key.startsWith("x-oss-")) return false;
    }
    return true;
  } catch { return false; }
}

const safePublicURL = z.string().refine(isSafeWorkbenchPublicURL, "URL 必须是不含凭据的公开 HTTPS 地址");
const optionalSafePublicURL = z.union([z.literal(""), safePublicURL]);
const timezone = nonEmpty(256).refine(value => {
  try { new Intl.DateTimeFormat("en-US", { timeZone: value }).format(); return true; } catch { return false; }
}, "时区必须可识别");
const commonSection = { key: nonEmpty(128), state: z.enum(["available", "missing", "unavailable"]), missing_reason: z.string().max(500) };
const changeSchema = z.object({ id: nonEmpty(1000), summary: nonEmpty(1000), observed_at: timestamp }).strict();
const changesV1Schema = z.object({ changes: z.array(changeSchema) }).strict();
const changesV2Schema = z.object({ changes: z.array(changeSchema).max(50), observed_event_count: z.number().int().nonnegative(), truncated: z.boolean() }).strict();
const month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/u);
const canonicalNonNegativeDecimal = z.string().regex(/^(0|[1-9]\d*)$/u).refine(value => BigInt(value) <= 9_223_372_036_854_775_807n, "数值超出最小货币单位范围");
const canonicalPositiveDecimal = canonicalNonNegativeDecimal.refine(value => value !== "0", "数值必须大于零");
const evidenceItemSchema = z.object({ id: nonEmpty(128), label: nonEmpty(1000), summary: nonEmpty(4000) }).strict();
const coverageDataSchema = z.object({ state: z.enum(["complete", "partial", "unknown"]), reason_code: nonEmpty(256) }).strict();
const openingStepLabels = {
  business_profile: "完善经营主体资料",
  market_eligibility: "确认目标市场资格",
  identity_verification: "完成主体身份核验",
  tax_profile: "完善税务资料",
  payout_account: "设置收款账户",
  store_review: "等待店铺审核",
} as const;
const openingGapLabels = {
  business_registration: "经营主体证明",
  identity_document: "主体身份证明",
  tax_information: "税务资料",
  payout_account: "收款账户资料",
  address_proof: "经营地址证明",
} as const;
const openingStepSchema = z.object({
  key: z.enum(Object.keys(openingStepLabels) as [keyof typeof openingStepLabels, ...(keyof typeof openingStepLabels)[]]),
  label: nonEmpty(1000),
  state: z.enum(["pending", "in_progress", "completed", "blocked"]),
  ordinal: z.number().int().positive(),
}).strict().superRefine((step, ctx) => {
  if (step.label !== openingStepLabels[step.key]) ctx.addIssue({ code: "custom", message: "开店步骤标签与固定步骤不一致", path: ["label"] });
});
const openingStepsDataSchema = z.object({ items: z.array(openingStepSchema).min(1).max(30) }).strict().superRefine((data, ctx) => {
  const keys = data.items.map(item => item.key);
  if (new Set(keys).size !== keys.length) ctx.addIssue({ code: "custom", message: "开店步骤不能重复", path: ["items"] });
  data.items.forEach((item, index) => {
    if (item.ordinal !== index + 1) ctx.addIssue({ code: "custom", message: "开店步骤序号必须连续", path: ["items", index, "ordinal"] });
  });
});
const openingGapSchema = z.object({
  code: z.enum(Object.keys(openingGapLabels) as [keyof typeof openingGapLabels, ...(keyof typeof openingGapLabels)[]]),
  label: nonEmpty(1000),
  required: z.boolean(),
}).strict().superRefine((gap, ctx) => {
  if (gap.label !== openingGapLabels[gap.code]) ctx.addIssue({ code: "custom", message: "材料标签与固定材料类型不一致", path: ["label"] });
});
const strategyDimensionKeys = ["channel", "audience", "budget", "product", "content", "promotion"] as const;
const strategyDimensionSchema = z.object({ key: z.enum(strategyDimensionKeys), headline: nonEmpty(500), summary: z.string().max(2000) }).strict();
const policyRuleSchema = z.object({ id: nonEmpty(128), label: nonEmpty(1000), state: z.enum(["compliant", "attention", "unknown"]) }).strict();
const policyEventSchema = z.object({ id: nonEmpty(128), summary: nonEmpty(2000), observed_at: timestamp }).strict();

export const workbenchSectionSchema = z.discriminatedUnion("kind", [
  z.object({ ...commonSection, kind: z.literal("text"), data: z.object({ paragraphs: z.array(nonEmpty(4000)).min(1) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("storyboard"), data: z.object({ frames: z.array(z.object({ index: z.number().int().nonnegative(), visual: nonEmpty(4000), dialogue: nonEmpty(4000) }).strict()).min(1) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("draft"), data: z.object({ draft_id: nonEmpty(1000), provider: nonEmpty(256), status: nonEmpty(256), requires_confirmation: z.boolean() }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("events"), data: z.object({ timezone, events: z.array(z.object({ id: nonEmpty(1000), title: nonEmpty(1000), date: dateOnly, all_day: z.literal(true), region: nonEmpty(500), note: z.string().max(4000), timezone, source_url: safePublicURL }).strict()).min(1) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("exchange_rate"), data: z.object({ base_currency: z.enum(["USD", "GBP"]), quote_currency: z.literal("CNY"), rate: z.number().positive(), date: dateOnly, stale: z.boolean(), reference_only: z.literal(true), source_url: safePublicURL }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("objects"), data: z.object({ object_count: z.number().int().positive(), objects: z.array(z.object({ id: nonEmpty(1000), label: nonEmpty(1000), state: nonEmpty(256) }).strict()).min(1) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("tracking_status"), data: z.object({ sync_state: z.enum(["pull_contract_pending", "observed_events_partial"]), observed_event_count: z.number().int().nonnegative().nullable(), window_hours: z.literal(24), complete: z.literal(false) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("changes"), data: z.union([changesV1Schema, changesV2Schema]).nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("metrics"), data: z.object({ metrics: z.array(z.object({ label: nonEmpty(500), value: z.number(), unit: nonEmpty(128), comparison_period: nonEmpty(500) }).strict()).min(1) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("monthly_structure"), data: z.object({ month, opportunities: z.array(z.object({ id: nonEmpty(128), title: nonEmpty(500), summary: nonEmpty(4000) }).strict()).min(1).max(50) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("evidence"), data: z.object({ items: z.array(evidenceItemSchema).min(1).max(50) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("coverage"), data: coverageDataSchema.nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("opening_progress"), data: z.object({ case_id: nonEmpty(128), provider: z.enum(["tiktok_shop", "amazon_sp", "shopify", "shoplazza", "shopee"]), market: z.string().regex(/^[A-Z]{2}$/u), status: z.enum(["not_started", "in_progress", "needs_documents", "blocked", "completed"]), revision: canonicalPositiveDecimal }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("opening_steps"), data: openingStepsDataSchema.nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("material_gaps"), data: z.object({ items: z.array(openingGapSchema).min(1).max(30) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("pricing_assumptions"), data: z.object({ formula_version: z.literal("merchant-pricing.v1"), currency: z.string().regex(/^[A-Z]{3}$/u), product_cost_minor: canonicalNonNegativeDecimal, logistics_minor: canonicalNonNegativeDecimal }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("pricing_breakdown"), data: z.object({ tax_bps: z.number().int().nonnegative().max(9999), commission_bps: z.number().int().nonnegative().max(9999), target_margin_bps: z.number().int().nonnegative().max(9999) }).strict().superRefine((data, ctx) => {
    if (data.tax_bps + data.commission_bps + data.target_margin_bps >= 10000) ctx.addIssue({ code: "custom", message: "税费、佣金和目标利润之和必须小于 100%" });
  }).nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("pricing_result"), data: z.object({ preview_id: nonEmpty(128), revision: canonicalPositiveDecimal, recommended_price_minor: canonicalPositiveDecimal }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("selection_candidates"), data: z.object({ items: z.array(z.object({ id: nonEmpty(128), label: nonEmpty(500), rationale: nonEmpty(4000), score_bps: z.number().int().min(0).max(10000) }).strict()).min(1).max(50) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("creator_candidates"), data: z.object({ items: z.array(z.object({ id: nonEmpty(128), display_label: nonEmpty(500), rationale: nonEmpty(4000), match_bps: z.number().int().min(0).max(10000) }).strict()).min(1).max(50) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("report_snapshot"), data: z.object({ snapshot_id: nonEmpty(128), snapshot_version: canonicalPositiveDecimal, snapshot_as_of: timestamp }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("report_status"), data: z.object({ state: z.literal("available") }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("report_resource"), data: z.object({ resource_id: nonEmpty(128), display_name: nonEmpty(500), mime_type: z.literal("application/pdf") }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("strategy_dimensions"), data: z.object({ items: z.array(strategyDimensionSchema).min(1).max(6) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("strategy_source_state"), data: z.object({ scope_id: nonEmpty(128), source_version: nonEmpty(1000), missing_dimensions: z.array(z.enum(strategyDimensionKeys)).max(5) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("policy_rules"), data: z.object({ items: z.array(policyRuleSchema).min(1).max(100) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("policy_events"), data: z.object({ items: z.array(policyEventSchema).max(100) }).strict().nullable() }).strict(),
  z.object({ ...commonSection, kind: z.literal("policy_status"), data: z.object({ mode: z.literal("management_only"), pull_complete: z.literal(false) }).strict().nullable() }).strict(),
  ...storeManagementSectionSchemas,
]).superRefine((section, ctx) => {
  if (section.state === "available") {
    if (section.data === null || section.missing_reason !== "") ctx.addIssue({ code: "custom", message: "可用栏目必须有资料且不能带缺失原因" });
  } else if (section.data !== null || !section.missing_reason.trim()) {
    ctx.addIssue({ code: "custom", message: "缺失或不可用栏目必须提供原因且资料为 null" });
  }
  if (section.state !== "available" || section.data === null) return;
  if (section.kind === "storyboard") {
    const indexes = section.data.frames.map(frame => frame.index);
    if (new Set(indexes).size !== indexes.length) ctx.addIssue({ code: "custom", message: "分镜序号不能重复", path: ["data", "frames"] });
  }
  if (section.kind === "events") {
    const ids = section.data.events.map(event => event.id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "日历事件标识不能重复", path: ["data", "events"] });
    section.data.events.forEach((event, index) => {
      if (event.timezone !== section.data?.timezone) ctx.addIssue({ code: "custom", message: "日历事件时区必须与栏目一致", path: ["data", "events", index, "timezone"] });
    });
  }
  if (section.kind === "objects") {
    const ids = section.data.objects.map(object => object.id);
    if (section.data.object_count !== section.data.objects.length) ctx.addIssue({ code: "custom", message: "对象数量必须与列表一致", path: ["data", "object_count"] });
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "对象标识不能重复", path: ["data", "objects"] });
  }
  if (section.kind === "tracking_status") {
    const countMatches = section.data.sync_state === "pull_contract_pending" ? section.data.observed_event_count === null : section.data.observed_event_count !== null;
    if (!countMatches) ctx.addIssue({ code: "custom", message: "同步状态与观察数量不一致", path: ["data", "observed_event_count"] });
  }
  if (section.kind === "changes") {
    const ids = section.data.changes.map(change => change.id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "变化事件标识不能重复", path: ["data", "changes"] });
    if ("observed_event_count" in section.data && (section.data.observed_event_count < section.data.changes.length || section.data.truncated !== (section.data.observed_event_count > section.data.changes.length))) ctx.addIssue({ code: "custom", message: "变化总数、截断状态与显示列表不一致", path: ["data"] });
  }
  if (section.kind === "monthly_structure") {
    const ids = section.data.opportunities.map(item => item.id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "月度机会标识不能重复", path: ["data", "opportunities"] });
  }
  if (section.kind === "evidence") {
    const ids = section.data.items.map(item => item.id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "证据标识不能重复", path: ["data", "items"] });
  }
  if (section.kind === "material_gaps") {
    const codes = section.data.items.map(item => item.code);
    if (new Set(codes).size !== codes.length) ctx.addIssue({ code: "custom", message: "材料缺口不能重复", path: ["data", "items"] });
  }
  if (section.kind === "selection_candidates" || section.kind === "creator_candidates") {
    const ids = section.data.items.map(item => item.id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "候选标识不能重复", path: ["data", "items"] });
  }
  if (section.kind === "strategy_dimensions") {
    const keys = section.data.items.map(item => item.key);
    if (new Set(keys).size !== keys.length) ctx.addIssue({ code: "custom", message: "策略维度不能重复", path: ["data", "items"] });
  }
  if (section.kind === "strategy_source_state") {
    const positions = section.data.missing_dimensions.map(key => strategyDimensionKeys.indexOf(key));
    if (new Set(section.data.missing_dimensions).size !== section.data.missing_dimensions.length || positions.some((position, index) => index > 0 && position <= positions[index - 1]!)) ctx.addIssue({ code: "custom", message: "缺失策略维度必须唯一并按固定顺序返回", path: ["data", "missing_dimensions"] });
  }
  if (section.kind === "policy_rules" || section.kind === "policy_events") {
    const ids = section.data.items.map(item => item.id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "政策条目标识不能重复", path: ["data", "items"] });
  }
  if (section.kind === "product_ranking") {
    const ids = section.data.items.map(item => item.product_id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "商品排行标识不能重复", path: ["data", "items"] });
  }
  if (section.kind === "low_stock_items") {
    const ids = section.data.items.map(item => `${item.product_id}\0${item.sku_id}`);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "低库存 SKU 不能重复", path: ["data", "items"] });
  }
  if (section.kind === "product_findings") {
    const ids = section.data.items.map(item => item.finding_id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "商品诊断标识不能重复", path: ["data", "items"] });
  }
  if (section.kind === "safe_evidence") {
    const ids = section.data.items.map(item => item.evidence_id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "诊断证据标识不能重复", path: ["data", "items"] });
  }
  if (section.kind === "ad_accounts") {
    const ids = section.data.items.map(item => item.ad_account_binding_id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "广告账户不能重复", path: ["data", "items"] });
  }
  if (section.kind === "ad_campaigns") {
    const ids = section.data.items.map(item => item.campaign_id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "广告计划不能重复", path: ["data", "items"] });
  }
  if (section.kind === "fulfillment_exceptions") {
    const ids = section.data.items.map(item => item.package_id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "履约异常包裹不能重复", path: ["data", "items"] });
  }
  if (section.kind === "status_mapping") {
    const statuses = section.data.items.map(item => item.source_status);
    if (new Set(statuses).size !== statuses.length) ctx.addIssue({ code: "custom", message: "来源状态映射不能重复", path: ["data", "items"] });
  }
  if (section.kind === "after_sales_aging") {
    const expected = ["under_24h", "one_to_three_days", "over_three_days"] as const;
    section.data.buckets.forEach((bucket, index) => {
      if (bucket.key !== expected[index]) ctx.addIssue({ code: "custom", message: "售后时效桶必须按固定顺序返回", path: ["data", "buckets", index, "key"] });
    });
  }
  if (section.kind === "safe_todos") {
    const ids = section.data.items.map(item => item.todo_id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "售后待办不能重复", path: ["data", "items"] });
  }
  if (section.kind === "social_accounts" || section.kind === "email_accounts") {
    const ids = section.data.items.map(item => item.connection_id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "连接标识不能重复", path: ["data", "items"] });
  }
});

const actionCommon = { label: nonEmpty(500), enabled: z.boolean(), disabled_reason: z.string().max(500) };
export const workbenchActionSchema = z.discriminatedUnion("kind", [
  z.object({ ...actionCommon, kind: z.literal("scope_navigation"), target: z.enum(["market", "shop"]) }).strict(),
  z.object({ ...actionCommon, kind: z.literal("conversation_intent"), intent_type: merchantHomeIntentTypeSchema, action_purpose: actionPurposeSchema.optional(), reference: nonEmpty(1000), expected_revision: nonEmpty(1000).optional() }).strict(),
  z.object({ ...actionCommon, kind: z.literal("conversation_navigation"), conversation_id: nonEmpty(1000) }).strict(),
  z.object({ ...actionCommon, kind: z.literal("resource_navigation"), resource_id: nonEmpty(128) }).strict(),
]);

const sourceRefSchema = z.object({
  kind: nonEmpty(256),
  id: nonEmpty(1000),
  label: nonEmpty(1000),
  url: optionalSafePublicURL,
  as_of: z.union([timestamp, dateOnly]).nullable(),
}).strict();
const mediaSchema = z.object({
  media_type: z.enum(["image", "video"]),
  public_url: safePublicURL,
  viewer_url: safePublicURL,
  relation: z.enum(["EXACT_PRODUCT", "EXACT_CATEGORY", "OPTIONAL_DIRECT_CONTEXT", "ANALOG_ONLY"]),
}).strict();
const legacyCardTypes = new Set<string>(merchantHomeLegacyCardTypes);
const capabilityCardTypes = new Set<string>(merchantHomeCapabilityCardTypes);
const storeManagementCardTypes = new Set<string>(merchantHomeStoreManagementCardTypes);
const capabilityPurposeByType: Record<string, z.infer<typeof actionPurposeSchema>> = {
  monthly_opportunity: "plan_monthly_opportunity",
  store_opening: "continue_store_opening",
  pricing_calculator: "plan_pricing_recalculation",
  product_selection_analysis: "plan_product_selection",
  creator_recommendation: "plan_creator_evaluation",
  content_strategy: "review_content_strategy",
  policy_compliance: "review_policy_compliance",
};
const storeActionByType: Record<string, { purpose: z.infer<typeof actionPurposeSchema>; label: string; candidateKind: string }> = {
  product_diagnosis: { purpose: "plan_product_price_update", label: "制定商品改价方案", candidateKind: "product_findings" },
  advertising_management: { purpose: "plan_ad_campaign_adjustment", label: "制定广告调整方案", candidateKind: "ad_campaigns" },
  inventory_management: { purpose: "plan_inventory_adjustment", label: "制定库存处理方案", candidateKind: "low_stock_items" },
  fulfillment_logistics: { purpose: "plan_fulfillment_shipment", label: "制定履约发货方案", candidateKind: "fulfillment_exceptions" },
  after_sales_reviews: { purpose: "plan_aftersales_decision", label: "制定售后处理方案", candidateKind: "safe_todos" },
  social_media: { purpose: "plan_social_publish_or_reply", label: "制定社媒发布或回复方案", candidateKind: "social_accounts" },
  email_management: { purpose: "plan_email_send_or_reply", label: "在对话中发信", candidateKind: "email_accounts" },
};

function actionKey(action: z.infer<typeof workbenchActionSchema>) {
  if (action.kind === "scope_navigation") return `${action.kind}\0${action.target}\0`;
  if (action.kind === "conversation_navigation") return `${action.kind}\0${action.conversation_id}\0`;
  if (action.kind === "resource_navigation") return `${action.kind}\0${action.resource_id}\0`;
  return `${action.kind}\0${action.intent_type}\0${action.action_purpose ?? ""}`;
}

export const workbenchContentSchema = z.object({
  content_version: z.union([z.literal(1), z.literal(2)]),
  content_id: nonEmpty(1000),
  card_type: merchantHomeWorkbenchCardTypeSchema,
  state: contentStateSchema,
  title: nonEmpty(500),
  summary: nonEmpty(4000),
  error_code: z.string().max(256),
  stale: z.boolean(),
  sections: z.array(workbenchSectionSchema),
  media: mediaSchema.nullable(),
  source_refs: z.array(sourceRefSchema),
  published_at: timestamp.nullable(),
  expires_at: timestamp.nullable(),
  updated_at: timestamp.nullable(),
  actions: z.array(workbenchActionSchema),
  source_status: sourceStatusSchema.nullable().optional(),
}).strict().superRefine((item, ctx) => {
  const isStoreManagement = storeManagementCardTypes.has(item.card_type);
  const hasSourceStatusField = item.source_status !== undefined;
  if (item.content_version === 1 && hasSourceStatusField) ctx.addIssue({ code: "custom", message: "v1 内容不能携带来源状态", path: ["source_status"] });
  if (item.content_version === 1 && !legacyCardTypes.has(item.card_type)) ctx.addIssue({ code: "custom", message: "v1 仅兼容原有八类卡片", path: ["content_version"] });
  if (item.content_version === 2 && !isStoreManagement && (!hasSourceStatusField || item.source_status === null || !legacySourceStatusSchema.safeParse(item.source_status).success)) ctx.addIssue({ code: "custom", message: "旧卡 v2 必须携带旧版来源状态", path: ["source_status"] });
  if (isStoreManagement) {
    if (item.content_version !== 2) ctx.addIssue({ code: "custom", message: "经营管理卡只接受 v2 内容", path: ["content_version"] });
    if (item.state === "ready") {
      const sourceStatus = storeManagementSourceStatusSchema.safeParse(item.source_status);
      if (!sourceStatus.success) ctx.addIssue({ code: "custom", message: "就绪经营管理卡必须携带经营快照来源状态", path: ["source_status"] });
      else {
        if (sourceStatus.data.snapshot_schema !== `merchant_home.${item.card_type}.v1`) ctx.addIssue({ code: "custom", message: "经营快照 schema 必须与卡片类型一致", path: ["source_status", "snapshot_schema"] });
        const identity = new RegExp(`^${item.card_type}:(mhss_[a-z0-9]+)-r([1-9]\\d*)$`, "u").exec(item.content_id);
        if (!identity || BigInt(identity[2] ?? "0") > 9_223_372_036_854_775_807n) ctx.addIssue({ code: "custom", message: "经营卡内容标识必须绑定快照及正整数修订", path: ["content_id"] });
        const sourceRef = item.source_refs[0];
        const sameAsOf = sourceRef?.as_of && sourceStatus.data.as_of && item.published_at
          && sourceTimeNanos(sourceRef.as_of) === sourceTimeNanos(sourceStatus.data.as_of)
          && sourceTimeNanos(sourceRef.as_of) === sourceTimeNanos(item.published_at);
        if (item.source_refs.length !== 1 || !sourceRef || sourceRef.kind !== "merchant_home_source_snapshot"
          || sourceRef.id !== identity?.[1] || sourceRef.label !== "经营数据安全快照" || sourceRef.url !== "" || !sameAsOf) {
          ctx.addIssue({ code: "custom", message: "经营卡只能引用与内容标识和发布时间一致的安全快照", path: ["source_refs"] });
        }
        const expiresAtMatches = item.expires_at && sourceStatus.data.freshness.stale_after
          && sourceTimeNanos(item.expires_at) === sourceTimeNanos(sourceStatus.data.freshness.stale_after);
        if (!expiresAtMatches) ctx.addIssue({ code: "custom", message: "经营卡过期时间必须与快照新鲜度阈值一致", path: ["expires_at"] });
        item.sections.forEach((section, index) => {
          if (section.state !== "available" || section.data === null) ctx.addIssue({ code: "custom", message: "就绪经营卡必须返回全部安全快照栏目", path: ["sections", index] });
        });
      }
    } else if (!hasSourceStatusField || item.source_status !== null) ctx.addIssue({ code: "custom", message: "非就绪经营管理卡必须显式返回空来源状态", path: ["source_status"] });
    const isAdvertising = item.card_type === "advertising_management" || item.card_type === "advertising_performance";
    if (isAdvertising && item.state === "requires_setup" && !["ad_account_selection_required", "ad_account_selection_unavailable", "source_sync_required", "source_sync_disabled", "source_sync_not_configured"].includes(item.error_code)) {
      ctx.addIssue({ code: "custom", message: "广告卡设置状态必须明确要求选择广告账户或等待采集快照", path: ["error_code"] });
    }
  }
  if (capabilityCardTypes.has(item.card_type) && item.content_version !== 2) ctx.addIssue({ code: "custom", message: "能力卡只接受带来源状态的 v2 内容", path: ["content_version"] });
  const allowed = Object.entries(sectionKinds[item.card_type]);
  if (!item.content_id.startsWith(`${item.card_type}:`) || item.content_id.length === item.card_type.length + 1) ctx.addIssue({ code: "custom", message: "内容标识与卡片类型不一致", path: ["content_id"] });
  if (item.sections.length !== allowed.length) ctx.addIssue({ code: "custom", message: "卡片必须返回完整固定栏目", path: ["sections"] });
  allowed.forEach(([key, kind], index) => {
    const section = item.sections[index];
    if (!section || section.key !== key || section.kind !== kind) ctx.addIssue({ code: "custom", message: "栏目键、类型或顺序与卡片合同不一致", path: ["sections", index] });
  });
  const changes = item.sections.find(section => section.kind === "changes");
  if (changes?.kind === "changes" && changes.data) {
    const hasV2Counts = "observed_event_count" in changes.data;
    if ((item.content_version === 2) !== hasV2Counts) ctx.addIssue({ code: "custom", message: "内容版本与变化计数字段不一致", path: ["sections"] });
  }

  const sourceKeys = item.source_refs.map(source => `${source.kind}\0${source.id}\0${source.url}`);
  if (new Set(sourceKeys).size !== sourceKeys.length) ctx.addIssue({ code: "custom", message: "来源引用不能重复", path: ["source_refs"] });
  if (item.state === "ready") {
    if (item.error_code !== "" || item.source_refs.length === 0) ctx.addIssue({ code: "custom", message: "就绪卡片必须有来源且不能带错误码" });
  } else {
    if (!item.error_code.trim() || item.stale || item.media !== null || item.source_refs.length || item.published_at !== null || item.expires_at !== null || item.updated_at !== null) ctx.addIssue({ code: "custom", message: "非就绪卡片只能保留状态资料" });
    const expectedSectionState = item.state === "unavailable" || item.state === "error" ? "unavailable" : "missing";
    item.sections.forEach((section, index) => {
      if (section.state !== expectedSectionState || section.data !== null) ctx.addIssue({ code: "custom", message: "非就绪卡片栏目状态不一致", path: ["sections", index] });
    });
  }
  if ((item.card_type === "media_reference" && item.state === "ready") !== (item.media !== null)) ctx.addIssue({ code: "custom", message: "媒体正文只能且必须属于就绪媒体参考", path: ["media"] });

  if (item.state === "ready" && capabilityCardTypes.has(item.card_type)) {
    const available = (kind: WorkbenchSection["kind"]) => item.sections.find(section => section.kind === kind && section.state === "available" && section.data !== null);
    const requireAvailable = (...kinds: WorkbenchSection["kind"][]) => kinds.forEach(kind => {
      if (!available(kind)) ctx.addIssue({ code: "custom", message: "就绪能力卡缺少必需栏目资料", path: ["sections"] });
    });
    if (item.card_type === "monthly_opportunity") {
      requireAvailable("monthly_structure", "evidence", "coverage");
      const section = available("coverage");
      if (section?.kind === "coverage" && section.data && item.source_status && (section.data.state !== item.source_status.coverage.state || section.data.reason_code !== item.source_status.coverage.reason_code)) ctx.addIssue({ code: "custom", message: "月度机会栏目与来源覆盖不一致", path: ["sections"] });
    }
    if (item.card_type === "store_opening") {
      requireAvailable("opening_progress", "opening_steps");
      const progress = available("opening_progress"); const steps = available("opening_steps"); const gaps = available("material_gaps");
      if (progress?.kind === "opening_progress" && progress.data && item.content_id !== `store_opening:${progress.data.case_id}-r${progress.data.revision}`) ctx.addIssue({ code: "custom", message: "开店 case、修订与内容标识不一致", path: ["content_id"] });
      if (progress?.kind === "opening_progress" && progress.data && steps?.kind === "opening_steps" && steps.data) {
        const allCompleted = steps.data.items.every(step => step.state === "completed");
        const hasInProgress = steps.data.items.some(step => step.state === "in_progress");
        const hasBlocked = steps.data.items.some(step => step.state === "blocked");
        const hasRequiredGap = gaps?.kind === "material_gaps" && gaps.data ? gaps.data.items.some(gap => gap.required) : false;
        const validState = progress.data.status === "completed" ? allCompleted && !hasRequiredGap
          : progress.data.status === "needs_documents" ? hasRequiredGap
            : progress.data.status === "blocked" ? hasBlocked
              : progress.data.status === "in_progress" ? hasInProgress && !allCompleted
                : steps.data.items.every(step => step.state === "pending");
        if (!validState) ctx.addIssue({ code: "custom", message: "开店进度、步骤与材料缺口不一致", path: ["sections"] });
      }
    }
    if (item.card_type === "pricing_calculator") {
      requireAvailable("pricing_assumptions", "pricing_breakdown", "pricing_result");
      const assumptions = available("pricing_assumptions"); const breakdown = available("pricing_breakdown"); const result = available("pricing_result");
      if (assumptions?.kind === "pricing_assumptions" && assumptions.data && breakdown?.kind === "pricing_breakdown" && breakdown.data && result?.kind === "pricing_result" && result.data) {
        const base = BigInt(assumptions.data.product_cost_minor) + BigInt(assumptions.data.logistics_minor);
        const denominator = 10000n - BigInt(breakdown.data.tax_bps + breakdown.data.commission_bps + breakdown.data.target_margin_bps);
        const expected = (base * 10000n + denominator - 1n) / denominator;
        if (item.content_id !== `pricing_calculator:${result.data.preview_id}-r${result.data.revision}` || expected !== BigInt(result.data.recommended_price_minor)) ctx.addIssue({ code: "custom", message: "定价结果与固定公式、修订或内容标识不一致", path: ["sections"] });
      }
    }
    if (item.card_type === "product_selection_analysis") requireAvailable("selection_candidates", "text", "evidence");
    if (item.card_type === "creator_recommendation") requireAvailable("creator_candidates", "text", "evidence");
    if (item.card_type === "report_export") {
      requireAvailable("report_snapshot", "report_status", "report_resource");
      const snapshot = available("report_snapshot");
      if (snapshot?.kind === "report_snapshot" && snapshot.data && (!item.source_status?.as_of || item.published_at === null || sourceTimeNanos(snapshot.data.snapshot_as_of) !== sourceTimeNanos(item.source_status.as_of) || sourceTimeNanos(snapshot.data.snapshot_as_of) !== sourceTimeNanos(item.published_at))) ctx.addIssue({ code: "custom", message: "报告快照时间必须与发布来源时间一致", path: ["sections"] });
    }
    if (item.card_type === "content_strategy") {
      requireAvailable("strategy_dimensions", "strategy_source_state");
      const dimensions = available("strategy_dimensions"); const sourceState = available("strategy_source_state");
      if (dimensions?.kind === "strategy_dimensions" && dimensions.data && sourceState?.kind === "strategy_source_state" && sourceState.data) {
        const present = new Set(dimensions.data.items.map(dimension => dimension.key));
        const expectedMissing = strategyDimensionKeys.filter(key => !present.has(key));
        if (expectedMissing.join("\0") !== sourceState.data.missing_dimensions.join("\0")) ctx.addIssue({ code: "custom", message: "策略维度与缺失维度不一致", path: ["sections"] });
        if (!new RegExp(`^content_strategy:${sourceState.data.scope_id.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&")}-v[0-9a-f]{32}$`, "u").test(item.content_id)) ctx.addIssue({ code: "custom", message: "策略范围、版本摘要与内容标识不一致", path: ["content_id"] });
      }
    }
    if (item.card_type === "policy_compliance") {
      requireAvailable("policy_rules", "policy_events", "coverage", "policy_status");
      const section = available("coverage");
      if (section?.kind === "coverage" && section.data && item.source_status && (section.data.state !== item.source_status.coverage.state || section.data.reason_code !== item.source_status.coverage.reason_code)) ctx.addIssue({ code: "custom", message: "合规栏目与来源覆盖不一致", path: ["sections"] });
    }
  }

  if (item.state === "ready" && isStoreManagement && item.card_type === "profit_settlement") {
    const components = item.sections.find(section => section.kind === "profit_components" && section.state === "available");
    const result = item.sections.find(section => section.kind === "profit_result" && section.state === "available");
    if (components?.kind !== "profit_components" || !components.data || result?.kind !== "profit_result" || !result.data) {
      ctx.addIssue({ code: "custom", message: "利润卡必须提供七项组成和确定性结果", path: ["sections"] });
    } else {
      const currencies = new Set(components.data.items.flatMap(component => component.currency === null ? [] : [component.currency]));
      const missing = components.data.items.filter(component => component.amount_minor === null).map(component => component.key);
      const expectedCurrency = currencies.size === 1 ? [...currencies][0]! : null;
      if (currencies.size > 1 || result.data.currency !== expectedCurrency) ctx.addIssue({ code: "custom", message: "利润组成与结果必须使用同一已知币种", path: ["sections"] });
      if (missing.join("\0") !== result.data.missing_component_keys.join("\0")) ctx.addIssue({ code: "custom", message: "利润缺失组成必须与七项事实一致", path: ["sections"] });
      const complete = missing.length === 0;
      if (result.data.complete !== complete || (complete !== (result.data.net_profit_minor !== null)) || (complete && result.data.currency === null)) ctx.addIssue({ code: "custom", message: "利润完整状态与组成事实不一致", path: ["sections"] });
      if (complete && result.data.net_profit_minor !== null) {
        const amounts = new Map(components.data.items.map(component => [component.key, BigInt(component.amount_minor!)]));
        const expected = (amounts.get("revenue") ?? 0n) - profitComponentKeys.slice(1).reduce((total, key) => total + (amounts.get(key) ?? 0n), 0n);
        if (BigInt(result.data.net_profit_minor) !== expected) ctx.addIssue({ code: "custom", message: "净利润必须由固定七项公式确定性计算", path: ["sections"] });
      }
      const expectedCoverage = complete ? { state: "complete", reason: "provider_complete" } : { state: "partial", reason: "profit_components_missing" };
      if (item.source_status?.coverage.state !== expectedCoverage.state || item.source_status.coverage.reason_code !== expectedCoverage.reason) ctx.addIssue({ code: "custom", message: "利润完整性必须与来源覆盖状态一致", path: ["source_status", "coverage"] });
    }
  }

  const storeActionSpec = storeActionByType[item.card_type];
  const storeCandidate = storeActionSpec
    ? item.sections.find(section => section.kind === storeActionSpec.candidateKind && section.state === "available")
    : undefined;
  const storeCandidateItems = storeCandidate?.data && typeof storeCandidate.data === "object" && "items" in storeCandidate.data
    ? (storeCandidate.data as { items?: unknown[] }).items
    : undefined;
  const storeExpectedRevision = storeActionSpec && item.state === "ready" && !item.stale
    && item.source_status?.freshness.state === "fresh" && Array.isArray(storeCandidateItems) && storeCandidateItems.length > 0
    ? /-r([1-9]\d*)$/u.exec(item.content_id)?.[1]
    : undefined;
  const expected = new Set<string>();
  if (isStoreManagement) {
    // manage/admin 权限不出现在 wire scope；无权用户合法收到 actions=[]。
    // 一旦服务端签发动作，仍必须是当前类型的唯一精确合同。
    if (item.actions.length > 0 && storeActionSpec && storeExpectedRevision) expected.add(`conversation_intent\0explore_opportunity\0${storeActionSpec.purpose}`);
  } else if (item.state === "ready") {
    if (item.card_type === "report_export") {
      const resource = item.actions.find(action => action.kind === "resource_navigation");
      if (!resource) ctx.addIssue({ code: "custom", message: "就绪报告必须返回云盘资源动作", path: ["actions"] });
      else expected.add(`resource_navigation\0${resource.resource_id}\0`);
    } else if (item.card_type === "auto_listing" && item.content_version === 2) {
      const conversation = item.actions.find(action => action.kind === "conversation_navigation");
      if (!conversation) ctx.addIssue({ code: "custom", message: "v2 就绪自动上品必须返回原会话动作", path: ["actions"] });
      else expected.add(`conversation_navigation\0${conversation.conversation_id}\0`);
    } else expected.add(`conversation_intent\0explore_opportunity\0${capabilityPurposeByType[item.card_type] ?? "discuss_opportunity"}`);
    if (item.card_type === "daily_product") expected.add("conversation_intent\0explore_opportunity\0plan_sample_test");
    if (item.card_type === "advertising_content") expected.add("conversation_intent\0explore_opportunity\0start_storyboard");
  } else if (item.state === "requires_binding") {
    if (item.card_type !== "auto_listing") ctx.addIssue({ code: "custom", message: "绑定状态只适用于自动上品", path: ["state"] });
    expected.add("conversation_intent\0bind_store\0");
  } else if (item.state === "requires_setup") {
    const publicMarketTypes: WorkbenchCardType[] = ["daily_product", "advertising_content", "promotion_calendar", "media_reference", "market_trends"];
    const trackingTypes: WorkbenchCardType[] = ["competitor_updates", "creator_tracking"];
    if (item.error_code === "market_required" && publicMarketTypes.includes(item.card_type)) expected.add("scope_navigation\0market\0");
    else if (item.error_code === "shop_selection_required" && item.card_type === "auto_listing") expected.add("scope_navigation\0shop\0");
    else if (item.error_code === "product_draft_required" && item.card_type === "auto_listing") expected.add("conversation_intent\0explore_opportunity\0");
    else if (["tracking_objects_required", "tracking_category_required", "tracking_scope_unknown"].includes(item.error_code) && trackingTypes.includes(item.card_type)) expected.add("conversation_intent\0configure_tracking\0");
    else ctx.addIssue({ code: "custom", message: "设置状态、错误码与卡片类型不一致", path: ["error_code"] });
  }
  const seenActions = new Set<string>();
  if (item.actions.length !== expected.size) ctx.addIssue({ code: "custom", message: "动作集合与卡片状态不一致", path: ["actions"] });
  item.actions.forEach((action, index) => {
    if ((action.enabled && action.disabled_reason !== "") || (!action.enabled && !action.disabled_reason.trim())) ctx.addIssue({ code: "custom", message: "动作可用状态与禁用原因不一致", path: ["actions", index] });
    if (action.kind === "scope_navigation" && !action.enabled) ctx.addIssue({ code: "custom", message: "范围动作必须可用", path: ["actions", index] });
    if (action.kind === "conversation_intent" && action.reference !== item.content_id) ctx.addIssue({ code: "custom", message: "动作引用与卡片内容不一致", path: ["actions", index, "reference"] });
    if (action.kind === "conversation_navigation" && (item.state !== "ready" || item.card_type !== "auto_listing" || !action.enabled)) ctx.addIssue({ code: "custom", message: "返回原会话动作只适用于就绪自动上品且必须可用", path: ["actions", index] });
    if (action.kind === "resource_navigation") {
      const resource = item.sections.find(section => section.kind === "report_resource");
      if (item.state !== "ready" || item.card_type !== "report_export" || !action.enabled || resource?.kind !== "report_resource" || resource.data?.resource_id !== action.resource_id) ctx.addIssue({ code: "custom", message: "资源动作只允许打开当前就绪报告的稳定云盘资源", path: ["actions", index] });
    }
    if (action.kind === "conversation_intent") {
      if (isStoreManagement) {
        if (!storeActionSpec || !storeExpectedRevision
          || action.intent_type !== "explore_opportunity" || action.action_purpose !== storeActionSpec.purpose
          || action.expected_revision !== storeExpectedRevision || action.label !== storeActionSpec.label
          || !action.enabled || action.disabled_reason !== "") {
          ctx.addIssue({ code: "custom", message: "经营管理卡只允许当前快照的领域规划动作", path: ["actions", index] });
        }
      } else {
        let expectedRevision: string | undefined;
        if (["monthly_opportunity", "product_selection_analysis", "creator_recommendation", "policy_compliance"].includes(item.card_type)) expectedRevision = /-v([1-9]\d*)$/u.exec(item.content_id)?.[1];
        if (item.card_type === "store_opening") {
          const progress = item.sections.find(section => section.kind === "opening_progress");
          expectedRevision = progress?.kind === "opening_progress" && progress.data ? progress.data.revision : undefined;
        }
        if (item.card_type === "pricing_calculator") {
          const result = item.sections.find(section => section.kind === "pricing_result");
          expectedRevision = result?.kind === "pricing_result" && result.data ? result.data.revision : undefined;
        }
        if (item.card_type === "content_strategy") {
          const state = item.sections.find(section => section.kind === "strategy_source_state");
          expectedRevision = state?.kind === "strategy_source_state" && state.data ? state.data.source_version : undefined;
        }
        if (capabilityPurposeByType[item.card_type]) {
          if (!expectedRevision || action.expected_revision !== expectedRevision) ctx.addIssue({ code: "custom", message: "能力动作必须绑定当前内容修订", path: ["actions", index, "expected_revision"] });
        } else if (action.expected_revision !== undefined) ctx.addIssue({ code: "custom", message: "旧卡动作不能附带能力修订", path: ["actions", index, "expected_revision"] });
      }
    }
    const key = actionKey(action);
    if (!expected.has(key) || seenActions.has(key)) ctx.addIssue({ code: "custom", message: "动作类型或业务目的不符合卡片合同", path: ["actions", index] });
    seenActions.add(key);
  });

  if (item.content_version !== 2 || !item.source_status) return;
  const status = item.source_status;
  const coverage = status.coverage;
  const freshness = status.freshness;
  if (item.stale !== (freshness.state === "stale")) ctx.addIssue({ code: "custom", message: "旧 stale 字段必须与来源新鲜度一致", path: ["stale"] });
  if (item.state !== "ready") {
    const reason = item.state === "unavailable" ? "source_unavailable" : item.state === "error" ? "source_read_failed" : "source_not_evaluated";
    if (status.window !== null || status.as_of !== null || status.source_revision !== null
      || freshness.state !== "unknown" || freshness.basis !== "not_evaluated" || freshness.stale_after !== null
      || coverage.state !== "unknown" || coverage.reason_code !== reason) ctx.addIssue({ code: "custom", message: "非就绪卡片必须使用对应的未评估来源状态", path: ["source_status"] });
    return;
  }

  if (isStoreManagement) return;

  const monitor = item.card_type === "competitor_updates" || item.card_type === "creator_tracking";
  if (monitor) {
    let validTiming = status.window !== null && exactDuration(status.window.start, status.window.end_exclusive, 24);
    if (validTiming && freshness.basis === "max_age" && status.window && status.as_of && freshness.stale_after) {
      const asOf = sourceTimeNanos(status.as_of)!; const start = sourceTimeNanos(status.window.start)!; const end = sourceTimeNanos(status.window.end_exclusive)!;
      validTiming = timestamp.safeParse(status.as_of).success && timestamp.safeParse(freshness.stale_after).success
        && exactDuration(status.as_of, freshness.stale_after, 24) && asOf < end
        && freshness.state === (asOf < start ? "stale" : "fresh");
    } else if (freshness.basis !== "source_time_unavailable") validTiming = false;
    if (!validTiming || coverage.state !== "partial" || coverage.reason_code !== "monitor_projection_without_remote_ack") ctx.addIssue({ code: "custom", message: "监控卡来源窗口或覆盖语义不符合合同", path: ["source_status"] });
    return;
  }
  const publishedAtMatches = status.as_of !== null && item.published_at !== null && timestamp.safeParse(status.as_of).success && sourceTimeNanos(status.as_of) === sourceTimeNanos(item.published_at);
  const updatedAtMatches = status.as_of !== null && item.updated_at !== null && timestamp.safeParse(status.as_of).success && sourceTimeNanos(status.as_of) === sourceTimeNanos(item.updated_at);
  if (["monthly_opportunity", "product_selection_analysis", "creator_recommendation", "report_export", "policy_compliance"].includes(item.card_type)) {
    const expiresAtMatches = freshness.stale_after !== null && item.expires_at !== null && timestamp.safeParse(freshness.stale_after).success && sourceTimeNanos(freshness.stale_after) === sourceTimeNanos(item.expires_at);
    const sourceTimesIncrease = status.as_of !== null && freshness.stale_after !== null && sourceTimeNanos(status.as_of)! < sourceTimeNanos(freshness.stale_after)!;
    const needsWindow = ["monthly_opportunity", "product_selection_analysis", "creator_recommendation"].includes(item.card_type);
    if (!publishedAtMatches || !expiresAtMatches || !sourceTimesIncrease || !status.source_revision || freshness.basis !== "max_age" || freshness.state === "unknown" || (needsWindow && status.window === null) || (item.card_type === "report_export" && status.window !== null)) ctx.addIssue({ code: "custom", message: "版本化能力结果的时间、窗口或修订不符合合同", path: ["source_status"] });
    const expectedReason = ({
      monthly_opportunity: "cwm_monthly_published_snapshot",
      product_selection_analysis: "versioned_product_selection_artifact",
      creator_recommendation: "versioned_creator_recommendation_artifact",
      report_export: "cloud_report_resource_available",
      policy_compliance: "policy_management_only",
    } as Record<string, string>)[item.card_type];
    const expectedState = item.card_type === "report_export" ? "complete" : item.card_type === "policy_compliance" ? "partial" : coverage.state;
    if (coverage.reason_code !== expectedReason || coverage.state !== expectedState) ctx.addIssue({ code: "custom", message: "版本化能力结果的覆盖语义不符合合同", path: ["source_status", "coverage"] });
    return;
  }
  if (["store_opening", "pricing_calculator", "content_strategy"].includes(item.card_type)) {
    let expectedState: WorkbenchSourceStatus["coverage"]["state"] = "complete";
    let expectedReason = item.card_type === "store_opening" ? "opening_case_safe_projection" : item.card_type === "pricing_calculator" ? "deterministic_pricing_preview" : "experience_strategy_current_safe_projection";
    if (item.card_type === "content_strategy") {
      const sourceState = item.sections.find(section => section.kind === "strategy_source_state");
      if (sourceState?.kind === "strategy_source_state" && sourceState.data?.missing_dimensions.length) {
        expectedState = "partial";
        expectedReason = "experience_strategy_current_missing_dimensions";
      }
    }
    if (status.window !== null || !updatedAtMatches || !status.source_revision || freshness.state !== "fresh" || freshness.basis !== "authoritative_read" || coverage.state !== expectedState || coverage.reason_code !== expectedReason) ctx.addIssue({ code: "custom", message: "本地能力读模型的来源状态不符合合同", path: ["source_status"] });
    return;
  }
  if (status.window !== null) ctx.addIssue({ code: "custom", message: "当前卡片不允许来源窗口", path: ["source_status", "window"] });
  const cwmTiming = publishedAtMatches && freshness.stale_after !== null && timestamp.safeParse(freshness.stale_after).success && exactDuration(status.as_of!, freshness.stale_after, 48);
  if (item.card_type === "daily_product" && (coverage.state !== "partial" || coverage.reason_code !== "cwm_daily_known_fields_only" || freshness.basis !== "max_age" || !cwmTiming)) ctx.addIssue({ code: "custom", message: "日批商品来源状态不符合合同", path: ["source_status"] });
  if (item.card_type === "advertising_content" && (coverage.state !== "partial" || coverage.reason_code !== "cwm_ad_cut_known_fields_only" || freshness.basis !== "max_age" || !cwmTiming)) ctx.addIssue({ code: "custom", message: "广告内容来源状态不符合合同", path: ["source_status"] });
  if (item.card_type === "market_trends" && (coverage.state !== "partial" || coverage.reason_code !== "cwm_trend_text_only" || freshness.basis !== "max_age" || !cwmTiming)) ctx.addIssue({ code: "custom", message: "市场趋势来源状态不符合合同", path: ["source_status"] });
  const draftTiming = status.as_of !== null && item.updated_at !== null && timestamp.safeParse(status.as_of).success && sourceTimeNanos(status.as_of) === sourceTimeNanos(item.updated_at);
  if (item.card_type === "auto_listing" && (coverage.state !== "complete" || coverage.reason_code !== "authorized_draft_scope_complete" || freshness.basis !== "authoritative_read" || !draftTiming)) ctx.addIssue({ code: "custom", message: "自动上品来源状态不符合合同", path: ["source_status"] });
  if (item.card_type === "promotion_calendar") {
    const events = item.sections.find(section => section.key === "events");
    const exchange = item.sections.find(section => section.key === "exchange_rate");
    const reason = events?.state === "available"
      ? "government_holidays_without_platform_campaigns"
      : events?.state === "unavailable" && exchange?.state === "available"
        ? "calendar_events_unavailable_exchange_rate_only"
        : "";
    if (!reason || coverage.state !== "partial" || coverage.reason_code !== reason || freshness.basis !== "source_time_unavailable") ctx.addIssue({ code: "custom", message: "经营日历来源状态不符合合同", path: ["source_status"] });
  }
  if (item.card_type === "media_reference" && (coverage.state !== "partial" || coverage.reason_code !== "qualified_media_without_coverage_contract" || freshness.basis !== "source_time_unavailable")) ctx.addIssue({ code: "custom", message: "媒体参考来源状态不符合合同", path: ["source_status"] });
}).transform(item => ({ ...item, source_status: item.content_version === 1 ? legacySourceStatus() : item.source_status ?? null }));

function sameScope(left: z.infer<typeof contentScopeSchema>, right: z.infer<typeof contentScopeSchema>) {
  return left.organization_id === right.organization_id && left.shop_binding_id === right.shop_binding_id
    && left.ad_account_binding_id === right.ad_account_binding_id
    && left.market === right.market && left.category === right.category && left.binding_state === right.binding_state;
}

export const workbenchGroupSchema = z.object({
  card_type: merchantHomeWorkbenchCardTypeSchema,
  state: contentStateSchema,
  effective_scope: contentScopeSchema,
  items: z.array(workbenchContentSchema).min(1).max(WORKBENCH_CONTENT_PAGE_LIMIT),
  total: z.number().int().nonnegative(),
  next_cursor: z.string(),
  has_more: z.boolean(),
}).strict().superRefine((group, ctx) => {
  const sentinel = group.total === 0 && group.items.length === 1 && group.items[0]?.state !== "ready";
  if (!sentinel && group.total < group.items.length) ctx.addIssue({ code: "custom", message: "总数不能小于当前页条目数", path: ["total"] });
  if (sentinel) {
    if (group.state !== group.items[0]?.state || group.has_more || group.next_cursor !== "") ctx.addIssue({ code: "custom", message: "非就绪状态哨兵的分页字段不一致" });
  } else {
    if (group.items.some(item => item.state !== "ready")) ctx.addIssue({ code: "custom", message: "普通内容页只能包含就绪条目", path: ["items"] });
    if (group.state !== "ready") ctx.addIssue({ code: "custom", message: "就绪分组状态与条目不一致", path: ["state"] });
  }
  if (group.items.some(item => item.card_type !== group.card_type)) ctx.addIssue({ code: "custom", message: "内容类型与分组不一致", path: ["items"] });
  const ids = group.items.map(item => item.content_id);
  if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", message: "分组内容标识不能重复", path: ["items"] });
  if (group.has_more) {
    if (!group.next_cursor || group.items.length !== WORKBENCH_CONTENT_PAGE_LIMIT) ctx.addIssue({ code: "custom", message: "有后页时必须返回完整页和游标" });
  } else if (group.next_cursor !== "") ctx.addIssue({ code: "custom", message: "末页不能返回游标", path: ["next_cursor"] });
});

export const workbenchContentResponseSchema = z.object({
  schema_version: z.union([z.literal(1), z.literal(2)]),
  effective_scope: contentScopeSchema,
  groups: z.array(workbenchGroupSchema).min(1).max(merchantHomeWorkbenchCardTypeSchema.options.length),
}).strict().superRefine((response, ctx) => {
  const groupTypes = response.groups.map(group => group.card_type);
  if (new Set(groupTypes).size !== groupTypes.length) ctx.addIssue({ code: "custom", message: "卡片分组不能重复", path: ["groups"] });
  const contentIDs: string[] = [];
  response.groups.forEach((group, index) => {
    if (!sameScope(response.effective_scope, group.effective_scope)) ctx.addIssue({ code: "custom", message: "顶层和分组范围必须完全一致", path: ["groups", index, "effective_scope"] });
    group.items.forEach((item, itemIndex) => {
      if (item.content_version !== response.schema_version) ctx.addIssue({ code: "custom", message: "响应版本与内容版本必须一致", path: ["groups", index, "items", itemIndex, "content_version"] });
    });
    contentIDs.push(...group.items.map(item => item.content_id));
  });
  if (new Set(contentIDs).size !== contentIDs.length) ctx.addIssue({ code: "custom", message: "响应内容标识不能跨分组重复", path: ["groups"] });
});

export type WorkbenchCardType = z.infer<typeof merchantHomeWorkbenchCardTypeSchema>;
export interface WorkbenchContentRequestContract {
  organizationId: string;
  shopBindingId?: string;
  adAccountBindingId?: string;
  market?: string;
  category?: string;
  cardTypes?: WorkbenchCardType[];
  cardType?: WorkbenchCardType;
  cursor?: string;
}

export const WORKBENCH_CONTENT_SCHEMA_INVALID = "workbench_content_schema_invalid";

export function parseWorkbenchContentResponse(raw: unknown, input: WorkbenchContentRequestContract) {
  try {
    return workbenchContentResponseSchema.superRefine((response, ctx) => {
    const requested = input.cardType ? [input.cardType] : input.cardTypes ?? [...merchantHomeWorkbenchCardTypeSchema.options];
    const invalidRequest = Boolean(input.cardType && input.cardTypes !== undefined)
      || Boolean(input.cursor && !input.cardType)
      || Boolean(input.adAccountBindingId !== undefined && !merchantAdAccountBindingIDSchema.safeParse(input.adAccountBindingId).success)
      || requested.length === 0
      || new Set(requested).size !== requested.length
      || requested.some(type => !merchantHomeWorkbenchCardTypeSchema.safeParse(type).success);
    if (invalidRequest) {
      ctx.addIssue({ code: "custom", message: "内容请求类型或游标组合无效", path: ["groups"] });
      return;
    }
    if (response.groups.length !== requested.length || response.groups.some((group, index) => group.card_type !== requested[index])) ctx.addIssue({ code: "custom", message: "响应分组必须与请求类型及顺序完全一致", path: ["groups"] });
    const scope = response.effective_scope;
    if (scope.organization_id !== input.organizationId
      || (input.shopBindingId !== undefined && scope.shop_binding_id !== input.shopBindingId)
      || scope.ad_account_binding_id !== input.adAccountBindingId
      || (input.market !== undefined && scope.market !== input.market)
      || (input.category !== undefined && scope.category !== input.category)) ctx.addIssue({ code: "custom", message: "响应有效范围与请求不一致", path: ["effective_scope"] });
    response.groups.forEach((group, index) => {
      const sentinel = group.total === 0 && group.items.length === 1 && group.items[0]?.state !== "ready";
      if (input.cursor && sentinel) ctx.addIssue({ code: "custom", message: "分页响应不能返回状态哨兵", path: ["groups", index] });
      if (!input.cursor && !sentinel && group.has_more !== (group.total > group.items.length)) ctx.addIssue({ code: "custom", message: "首批分页状态与总数不一致", path: ["groups", index] });
    });
    }).parse(raw);
  } catch {
    throw new Error(WORKBENCH_CONTENT_SCHEMA_INVALID);
  }
}

export type WorkbenchContentResponse = z.infer<typeof workbenchContentResponseSchema>;
export type WorkbenchContent = z.infer<typeof workbenchContentSchema>;
export type WorkbenchContentInput = z.input<typeof workbenchContentSchema>;
export type WorkbenchSourceStatus = z.infer<typeof sourceStatusSchema>;
export type WorkbenchLegacySourceStatus = z.infer<typeof legacySourceStatusSchema>;
export type WorkbenchStoreManagementSourceStatus = z.infer<typeof storeManagementSourceStatusSchema>;
export type WorkbenchSection = z.infer<typeof workbenchSectionSchema>;
export type WorkbenchGroup = z.infer<typeof workbenchGroupSchema>;
export type WorkbenchAction = z.infer<typeof workbenchActionSchema>;
export type ActionPurpose = z.infer<typeof actionPurposeSchema>;
